import { Department, Province } from "@nt-portal/doc-agent-sdk"
import { AuthenticationService, UserProfile } from "@nt-portal/server-sdk"
import { set } from "date-fns"
import { Button } from "primereact/button"
import { TreeNode } from "primereact/treenode"
import { TreeSelect, TreeSelectChangeEvent, TreeSelectProps } from "primereact/treeselect"
import { useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react"
import { FabrixIcon, findNode, translate } from "../../common"
import { ValidationInput, ValidationInputEvent } from ".."
import { useCurrentUser, useDataController, useHasRoles, useIcon, useUserPermissions, useUserProfile, useValidationGroup } from "../../hooks"
import { ClientDepartmentService, ClientProvinceService, ClientUserService, JwtAuthService } from "../../services"
import React from "react"
import FileUploader from "../file-uploader"

export interface UserProfileRef {
    save(): Promise<UserProfile>;

}

const createProfile = (): UserProfile => ({
    department: "",
    province: "",
    jobTitle: "",
    jobDescription: "",
    cellPhone: "",
    officeLocation: "",
    officePhone: "",
    user: {id: "", userName: "", name: "", roles: [], email: "", permissions: []}
});

export interface UserProfileInfoProps {
    showSave?: boolean;
    style?: React.CSSProperties;
    showImage?: boolean;
    userProfile?: UserProfile | null;
    mode: "admin" | "user"
    onChange?: (userProfile: UserProfile) => void;
}

export const UserProfileInfo = React.forwardRef((props: UserProfileInfoProps, ref) => {


    const icon = useIcon();
    const user = useCurrentUser()
    const [_userRoles, hasRoles] = useHasRoles();
    const permissions = useUserPermissions();
    const [userProfile, saveUserProfile] = useUserProfile()
    const [_userProfile, setUserProfile] = useState<UserProfile | null | undefined>(null);
    const [_provinces, , , provActions] = useDataController<Province>(new ClientProvinceService())
    const [_departments, _total, _busy, actions] = useDataController<Department>(new ClientDepartmentService())
    const [_errorMessage, setErrorMessage] = useState<string>("");
    const [_isValid, updateValidation, registerValidation] = useValidationGroup();
    const [_nodes, setNodes] = useState<TreeNode[]>([]);
    const _disableDepartment = useMemo<boolean>(() => {
        if (props.mode === "admin")
            return false;
        return userProfile?.department ? true : false;
    }, [props.mode, userProfile?.department]);

    const _heading = useMemo(() => {
        let result = ""
        let noRoles = _userRoles.length === 0;
        let keys = Object.keys(permissions);
        let noPermissions = (keys.length === 0);
        let noDepartment = (userProfile?.department === null || userProfile?.department === undefined) 
        //There are 3 options

        // If the user has no roles and no permissions, request user profile completion.
        if (noRoles && noPermissions)
            result = translate('USER_INFO_REQUEST');
        // If the user has roles and a department but no permissions, indicate incorrect rights.
        else if (!noRoles && noPermissions && !noDepartment)
            result = translate('USER_INCORRECT_RIGHTS');
        // If the user has roles, permissions, but no department, request user profile completion.
        else if (!noRoles && !noPermissions && noDepartment)
            result = translate('USER_PROFILE_REQUEST');

        return result;

    }, [permissions, userProfile, _userRoles])


    useImperativeHandle(ref, () => ({
        async save() {
            if (!_userProfile) return;
            await saveUserProfile(_userProfile);
            return _userProfile;
        }
    }));

    useEffect(() => {

        registerValidation([
            { id: "job-title", isValid: userProfile?.jobTitle ? true : false },
            { id: "department", isValid: userProfile?.department ? true : false }
        ]);
    }, [userProfile]);

    useEffect(() => {
        if (props.mode === "admin") {
            let profile: UserProfile = props.userProfile ?? createProfile();
            setUserProfile(profile);
        }
        if (props.mode === "user")
            setUserProfile(userProfile);


    }, [props.userProfile, userProfile]);

    useEffect(() => {
        if (props.onChange && _userProfile) {
            props.onChange(_userProfile);
        }
    }, [_userProfile]);


    useEffect(() => {
        if (JwtAuthService.isAuthenticated()) {
            if (_provinces.length === 0)
                provActions.getLookup({})
            if (_departments.length === 0)
                actions.getLookup({})
        }
    }, [user]);

    useEffect(() => {
        let nodes: TreeNode[] = _provinces.map(prov => ({
            key: prov.abbreviation,
            label: prov.name,
            selectable: false,
            style: { fontWeight: "bold" },
            data: { ...prov, type: "province" }
        }));
        for (let node of nodes) {
            node.children = _departments.filter(dept => dept.province.abbreviation === node.key).map(dept => ({
                key: dept.shortName,
                label: dept.name,
                style: { fontWeight: "normal" },
                data: { ...dept, type: "department" },
            }));
        }
        setNodes(nodes);
    }, [_departments, _provinces]);

    const handleUpdate = (e: ValidationInputEvent) => {
        let profile = { ..._userProfile } as UserProfile;
        let id = (e.originalEvent.target as HTMLElement).id;
        let isValid = false;

        switch (id) {
            case "job-title":
                profile.jobTitle = e.value;
                isValid = profile.jobTitle ? true : false;
                updateValidation(id, isValid);
                break;
            case "job-description":
                profile.jobDescription = e.value;
                break;
            case "department":
                let dept = _departments.find(d => d.shortName === e.value);
                profile.department = dept ? dept.shortName : "";
                profile.province = dept ? dept.province.abbreviation : "";
                isValid = profile.department ? true : false;
                updateValidation(id, isValid);
                break;
        }
        setUserProfile(profile);


    }

    const handleSearchOptions = async (query: string, data: any[]) => {
        let promise = new Promise<Department[]>((resolve, reject) => {
            let items = data as Department[];
            let result = items.filter((x) => {
                let lcQuery = query.toLocaleLowerCase();
                let matchName = x.name?.toLocaleLowerCase().indexOf(lcQuery) !== -1;
                return matchName;
            });
            resolve(result);
        });
        return promise;
    }

    const handleImageSelected = async (file: File) => {
        const imageBase64 = Buffer.from(await file.arrayBuffer()).toString("base64");
        let profile = { ..._userProfile } as UserProfile;
        profile.imageBase64 = imageBase64;
        profile.imageMimeType = file.type || "application/octet-stream";
        setUserProfile(profile);
    }

    const handleImageCleared = () => {
        let profile = { ..._userProfile } as UserProfile;
        profile.imageBase64 = null;
        profile.imageMimeType = "";
        setUserProfile(profile);
    }

    const handleValidationChanged = (isValid: boolean, id?: string) => {
        updateValidation(id!, isValid);
    }

    const itemTemplate = (node: TreeNode) => {
        if (node.data.type === "province")
            return node.data.name;
        else
            return `${node.data.name} (${node.data.province.abbreviation})`
    }

    const valueTemplate = (selectedNodes: TreeNode | TreeNode[], props: TreeSelectProps) => {
        if (Array.isArray(selectedNodes)) {
            return selectedNodes.map(node => `${node.data.name} (${node.data.province.abbreviation})`).join(", ");
        } else {
            let node = selectedNodes;
            return `${node.data.name} (${node.data.province.abbreviation})`
        }
    }


    const handleSave = async () => {
        if (!_userProfile) return;
        await saveUserProfile(_userProfile);
        let userService = new ClientUserService();
        await userService.syncRoles();
        let jwtAuthService = new JwtAuthService();
        await jwtAuthService.silentLogin();
        window.location.reload();
    }

    const handleChange = (e: TreeSelectChangeEvent) => {
        let node = findNode(e.value as string, _nodes);
        if (node && node.data) {
            if (node.data.type === "department") {
                let value = { ..._userProfile } as UserProfile;
                value.department = (node.data as Department).shortName;
                setUserProfile(value);
                const isValid = value.department ? true : false;
                updateValidation("department", isValid);
            }

        }
    }

    return (
        <div className="grid" style={props.style ?? { marginTop: "20px", width: "800px" }}>
            <div className="col-12">
                <div style={{ fontWeight: 600 }}>{_heading}</div>
            </div>
            <div className="col-6" >
                <ValidationInput errorMessage={_errorMessage} id={"job-title"} value={_userProfile?.jobTitle ?? ''} caption={"JOB_TITLE"} keyName={"title"}
                    required={true} onChange={handleUpdate} onValidationChanged={handleValidationChanged}
                />
            </div>
            <div className="col-6 field" >
                <label style={{}} className="item-authors" >{translate("DEPARTMENT")}</label>
                <TreeSelect value={_userProfile?.department ?? ''} disabled={_disableDepartment} style={{ width: "100%", height: 40 }} panelClassName="fbx-user-info-dept"
                    onChange={handleChange} nodeTemplate={itemTemplate} valueTemplate={valueTemplate}
                    options={_nodes} selectionMode="single"></TreeSelect>
            </div>
            <div className="col-12" >
                <ValidationInput errorMessage={_errorMessage} id={"job-description"} value={_userProfile?.jobDescription ?? ''} caption={"JOB_DESCRIPTION"} keyName={"title"}
                    required={true} onChange={handleUpdate}
                />
            </div>

            {props.showImage && (<div className="p-fluid col-12 field">
                <label>{translate("IMAGE")}</label>
                <FileUploader
                    accept="image/*"
                    mode="advanced"
                    buttons={["choose", "cancel"]}
                    onFileSelected={handleImageSelected}
                    onClear={handleImageCleared}
                />
            </div>)}
            {props.showSave && (
                <div className="col-12" >
                    <Button label={translate("SAVE")} style={{ float: "right" }} disabled={!_isValid} icon={icon(FabrixIcon.dialogSave)} onClick={handleSave} />
                </div>
            )}
        </div>
    )
})

export default UserProfileInfo;